import { Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { CATEGORIES, type Category as CategoryConst } from '@/lib/constants';

interface CategoryNavProps {
  active: CategoryConst;
  onSelect: (c: CategoryConst) => void;
  counts: Partial<Record<CategoryConst, number>>;
}

/**
 * Horizontal category nav — pills that scroll on small screens.
 * Active pill: solid Brix primary. Inactive: white card on brix-border with
 * primary-tint hover.
 */
export function CategoryNav({ active, onSelect, counts }: CategoryNavProps) {
  return (
    <div
      className="flex gap-2 overflow-x-auto scrollbar-thin pb-1 -mx-1 px-1"
      role="tablist"
    >
      {CATEGORIES.map((c) => {
        const isActive = active === c;
        const count = counts[c] ?? 0;
        // My Collection is personal — no pill until the customer has
        // ordered something (new accounts just see the plain catalog).
        if (c === 'My Collection' && count === 0) return null;
        return (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(c)}
            className={cn(
              'shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap',
              'transition-all duration-200 cursor-pointer',
              'flex items-center gap-1.5',
              isActive
                ? 'bg-brix-primary text-white shadow-card'
                : 'bg-brix-surface text-brix-ink border border-brix-border hover:bg-brix-primary-tint hover:border-brix-primary/30',
            )}
          >
            {c === 'My Collection' && (
              <Sparkles size={12} className={isActive ? 'text-white/80' : 'text-brix-accent'} />
            )}
            <span>{c}</span>
            <span
              className={cn(
                'text-[10px] font-mono',
                isActive ? 'text-white/70' : 'text-brix-muted',
              )}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
